import { Link } from "react-router-dom";
import type { VideoItem } from "@/mocks/videos";
import { formatCount, formatDate } from "@/utils/format";

interface VideoCardProps {
  video: VideoItem;
  horizontal?: boolean;
  className?: string;
}

export default function VideoCard({ video, horizontal = false, className = "" }: VideoCardProps) {
  if (horizontal) {
    return (
      <Link
        to={`/detail/${video.id}`}
        className={`group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-2.5 transition-colors hover:border-primary-300 hover:bg-primary-50/40 ${className}`}
      >
        <div className="relative h-20 w-32 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={video.cover}
            alt={video.title}
            title={`${video.title} 吃瓜视频`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute bottom-1 right-1 rounded bg-foreground-950/70 px-1.5 py-0.5 text-[10px] text-background-50">
            {video.duration}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-between py-0.5">
          <h3 className="line-clamp-2 text-sm font-medium leading-5 text-foreground-900 transition-colors group-hover:text-primary-600">
            {video.title}
          </h3>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-play-line text-sm" />
              </span>
              {formatCount(video.views)}
            </span>
            <span>{formatDate(video.publishTime)}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${video.id}`}
      className={`group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300 ${className}`}
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-background-200">
        <img
          src={video.cover}
          alt={video.title}
          title={`${video.title} 吃瓜视频`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 rounded bg-primary-500 px-2 py-0.5 text-[11px] font-medium text-background-50">
          {video.categoryName}
        </span>
        <span className="absolute bottom-2 right-2 rounded bg-foreground-950/70 px-1.5 py-0.5 text-[11px] text-background-50">
          {video.duration}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-3.5">
        <h3 className="line-clamp-2 text-sm font-semibold leading-5 text-foreground-900 transition-colors group-hover:text-primary-600 md:text-[15px]">
          {video.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-xs leading-5 text-foreground-600">{video.summary}</p>
        <div className="mt-auto flex items-center justify-between pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-play-line text-sm" />
            </span>
            {formatCount(video.views)}
          </span>
          <span className="flex items-center gap-1">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-time-line text-sm" />
            </span>
            {formatDate(video.publishTime)}
          </span>
        </div>
      </div>
    </Link>
  );
}